import axios from 'axios'
import { api } from './client'

const BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

/** The register sign-in is for: exactly one of the two. */
export type SignInTarget = { bookingId?: string; sessionId?: string }

export type SignInWindowDto = {
  id: string
  code: string
  openedAt: string
  closesAt: string
  closedAt: string | null
  isOpen: boolean
}

export type SignInPersonDto = { id: string; name: string; email: string | null }

export type SignInItemStatus = 'MatchedByEmail' | 'NeedsReview' | 'Unmatched' | 'Confirmed' | 'WalkInAdded' | 'Dismissed'

export type SignInItemDto = {
  id: string
  firstName: string
  lastName: string
  email: string
  signedInAt: string
  status: SignInItemStatus
  needsAction: boolean
  candidates: SignInPersonDto[]
  suggestedStaff: SignInPersonDto | null
  hasPhoto: boolean
  photoSkipped: boolean
  recordedAs: string | null
}

export type SignInPhotoMode = 'none' | 'optional' | 'required'

export type SignInStatusDto = {
  window: SignInWindowDto | null
  expected: number
  signedIn: number
  pending: SignInItemDto[]
  recent: SignInItemDto[]
  canAddWalkIn: boolean
  photoMode: SignInPhotoMode
}

export type SignInResolution = 'Confirm' | 'WalkIn' | 'Dismiss'

export const signInApi = {
  status: (t: SignInTarget) => api.get<SignInStatusDto>('/attendance-signin', { params: t }).then(r => r.data),
  open: (t: SignInTarget, minutes?: number) => api.post<SignInWindowDto>('/attendance-signin/open', { ...t, minutes }).then(r => r.data),
  close: (windowId: string) => api.post(`/attendance-signin/windows/${windowId}/close`),
  resolve: (signInId: string, resolution: SignInResolution, ids: { attendeeId?: string; existingStaffMemberId?: string } = {}) =>
    api.post(`/attendance-signin/sign-ins/${signInId}/resolve`, { resolution, ...ids }),
  /** Photos need the sign-in token, so they are fetched as blobs rather than put in an <img src>. */
  photo: (signInId: string) =>
    api.get<Blob>(`/attendance-signin/sign-ins/${signInId}/photo`, { responseType: 'blob' }).then(r => r.data),
}

// The attendee's side: no sign-in.
export type PublicSignInDto = {
  open: boolean
  title: string
  organisation: string
  logoUrl: string | null
  primaryColour: string | null
  photoMode: SignInPhotoMode
  photoNotice: string | null
  closesAt: string | null
}

const publicApi = axios.create({ baseURL: BASE_URL })

export const publicSignInApi = {
  info: (code: string) => publicApi.get<PublicSignInDto>(`/public/signin/${encodeURIComponent(code)}`).then(r => r.data),
  submit: (code: string, form: { firstName: string; lastName: string; email: string; selfie?: Blob | null; couldNotTakePhoto?: boolean }) => {
    const body = new FormData()
    body.append('firstName', form.firstName)
    body.append('lastName', form.lastName)
    body.append('email', form.email)
    body.append('couldNotTakePhoto', String(!!form.couldNotTakePhoto))
    if (form.selfie) body.append('selfie', form.selfie, 'selfie.jpg')
    return publicApi.post(`/public/signin/${encodeURIComponent(code)}`, body)
  },
}
